import React, { useMemo } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getOptionLabel, getOptionValue, isOptionObject } from './optionUtils';

/**
 * Reusable Autocomplete wrapper for single + multi.
 *
 * Options can be:
 * - string[]
 * - { value, label }[]
 * - custom objects (pass `getOptionLabel` / `getOptionValue` if needed)
 */
export default function AutocompleteField({
  label,
  options = [],
  value,
  onChange,
  multiple = false,
  size = 'small',
  sx,
  placeholder,
  helperText,
  getOptionLabel: getOptionLabelProp,
  getOptionValue: getOptionValueProp,
  isOptionEqualToValue: isOptionEqualToValueProp,
  renderOption,
  renderTags: renderTagsProp,
  ...autocompleteProps
}) {
  const getLab = getOptionLabelProp || getOptionLabel;
  const getVal = getOptionValueProp || getOptionValue;

  const valueById = useMemo(() => {
    const m = new Map();
    for (const opt of options) {
      m.set(getVal(opt), opt);
    }
    return m;
  }, [options, getVal]);

  // Support controlled value as either:
  // - full option object(s)
  // - raw id(s) (value field)
  const normalizedValue = useMemo(() => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
      return arr.map((v) => (isOptionObject(v) ? v : (valueById.get(v) ?? null))).filter(Boolean);
    }
    if (value == null || value === '') return null;
    return isOptionObject(value) ? value : (valueById.get(value) ?? null);
  }, [value, multiple, valueById]);

  const isEq = isOptionEqualToValueProp || ((opt, val) => getVal(opt) === getVal(val));

  return (
    <Autocomplete
      multiple={multiple}
      size={size}
      sx={sx}
      options={options}
      value={normalizedValue}
      getOptionLabel={getLab}
      isOptionEqualToValue={isEq}
      renderOption={renderOption}
      onChange={(e, newValue) => {
        if (multiple) {
          onChange?.((newValue || []).map((v) => getVal(v)));
        } else {
          onChange?.(newValue ? getVal(newValue) : '');
        }
      }}
      renderTags={renderTagsProp || ((tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={getLab(option)}
            size="small"
            deleteIcon={<CloseIcon />}
            {...getTagProps({ index })}
          />
        ))
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
        />
      )}
      {...autocompleteProps}
    />
  );
}

